import { useTranslate } from "ra-core";
import { useEffect, useState, type KeyboardEvent } from "react";

import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

export interface InlineTextProps {
  value: string;
  /** Persists the new value; the field stays in edit mode if it throws. */
  onSave: (value: string) => Promise<void> | void;
  label: string;
  type?: "text" | "email" | "tel";
  multiline?: boolean;
  required?: boolean;
  className?: string;
  displayClassName?: string;
}

/**
 * Text shown as plain content that becomes an input when clicked. Enter (or
 * leaving the field) saves, Escape cancels.
 */
export const InlineText = ({
  value,
  onSave,
  label,
  type = "text",
  multiline = false,
  required = false,
  className,
  displayClassName,
}: InlineTextProps) => {
  const translate = useTranslate();
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  useEffect(() => {
    if (!isEditing) setDraft(value);
  }, [value, isEditing]);

  const save = async () => {
    const next = draft.trim();
    if (next === value.trim()) {
      setIsEditing(false);
      return;
    }
    if (required && !next) {
      setDraft(value);
      setIsEditing(false);
      return;
    }
    try {
      await onSave(next);
      setIsEditing(false);
    } catch {
      // keep editing so the user can fix the value
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === "Escape") {
      setDraft(value);
      setIsEditing(false);
    }
    if (
      event.key === "Enter" &&
      (!multiline || event.ctrlKey || event.metaKey)
    ) {
      event.preventDefault();
      save();
    }
  };

  if (isEditing) {
    const props = {
      autoFocus: true,
      value: draft,
      "aria-label": label,
      onChange: (event: { target: { value: string } }) =>
        setDraft(event.target.value),
      onBlur: save,
      onKeyDown: handleKeyDown,
      className,
    };
    return multiline ? (
      <Textarea {...props} />
    ) : (
      <Input type={type} {...props} />
    );
  }

  return (
    <button
      type="button"
      onClick={() => setIsEditing(true)}
      aria-label={label}
      title={label}
      className={cn(
        "w-full rounded-md px-1 py-0.5 text-left hover:bg-accent/60",
        !value && "text-muted-foreground",
        multiline && "whitespace-pre-wrap",
        displayClassName,
      )}
    >
      {value || translate("crm.operations.placeholders.empty_value")}
    </button>
  );
};
